import Drawer from "@via/components/drawer";
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useSyncExternalStore,
} from "react";
import { useNavigate } from "react-router-dom";
import { classNames } from "@via/utils/helpers";
import ThinkingAnimation from "@via/components/thinking-animation/thinking-animation";
import { XCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { useCortexContext } from "@via/context/cortex-context";
import { useCortexStreaming } from "@via/hooks/useCortexStreaming";
import SelectComponent from "@via/components/select/select";
import { SelectOption } from "@via/utils/interfaces";
import { useAvailableAIModels } from "@via/api/ai-models.api.tsx";
import { enableAiInsight } from "@via/components/cortex-drawer/ai-insight";
import { useKnowledgeBuilderAssistantsForDrawer } from "@via/hooks/useKnowledgeBuilderAssistantsForDrawer";
import { useAuth } from "@via/hooks/useAuth";
import { useIntegrationTokenById } from "@via/api/integration-tokens.api";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { ConversationTabs } from "@via/components/cortex-drawer/conversation-tabs";
import {
  DrawerModeBodies,
  DrawerModeToggle,
  drawerModeFor,
} from "@via/components/cortex-drawer/drawer-mode-toggle";
import { ClaudeCodePanel } from "@via/components/cortex-drawer/claude-code/claude-code-panel";
import {
  ConversationList,
  ConversationListButton,
} from "@via/components/cortex-drawer/conversation-list";
import { RouteChangePrompt } from "@via/components/cortex-drawer/route-change-prompt";
import { ConversationPagePrompt } from "@via/components/cortex-drawer/conversation-page-prompt";
import { ContextMismatchBanner } from "@via/components/cortex-drawer/context-mismatch-banner";
import { CortexMessageBubble } from "@via/components/cortex-drawer/cortex-message-bubble";
import { ApplySnippetPicker } from "@via/components/cortex-drawer/apply-snippet-picker";
import { McpToolConfirmation } from "@via/components/cortex-drawer/mcp-tool-confirmation";
import { McpToolCallBubble } from "@via/components/cortex-drawer/mcp-tool-call-bubble";
import {
  validatePageUrl,
  stripBasename,
} from "@via/components/cortex-drawer/helpers/utils";
import { MarkdownMessage } from "@via/components/markdown/markdown-message";
import { ServiceTokenType, DEFAULT_LLM_MODELS } from "common";
import { ROUTES } from "@via/utils/constants";
import {
  getCortexSelection,
  subscribeCortexSelection,
} from "@via/utils/cortex-selection";
import { Accordion } from "@via/components/accordion/accordion";
import {
  applyToCortexMonacoTarget,
  getCortexMonacoTargetForCurrentPage,
  subscribeCortexMonacoTarget,
} from "@via/utils/cortex-monaco-target";

const PREFERRED_MODELS = Object.values(DEFAULT_LLM_MODELS);

interface KnowledgeBuilderAssistantOption extends SelectOption {
  value: string;
  label: string;
  isDisabled?: boolean;
  model?: string;
}

interface CortexDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTokenId: number | null;
  usePortal?: boolean;
  onMinimizedChange?: (isMinimized: boolean) => void;
  onWidthChange?: (width: number | null) => void;
}

const CortexDrawer = ({
  isOpen,
  onClose,
  selectedTokenId,
  usePortal = true,
  onMinimizedChange,
  onWidthChange,
}: CortexDrawerProps) => {
  const {
    getCurrentContext,
    conversations,
    pagedConversations,
    activeConversationId,
    activeConversation,
    openTabIds,
    setActiveConversationId,
    createNewConversation,
    appendUserMessage,
    appendAssistantMessage,
    appendMessage,
    replaceConversationId,
    deleteConversation,
    setOpenTabIds,
    conversationsLoading,
    conversationListPinnedOnly,
    setConversationListPinnedOnly,
    conversationListPage,
    setConversationListPage,
    conversationListPageSize,
    setConversationListPageSize,
    conversationListSearchKey,
    setConversationListSearchKey,
    conversationListTotal,
    updateConversationMeta,
    drawerMode,
    setDrawerMode,
  } = useCortexContext();
  const currentContext = getCurrentContext();
  const { user } = useAuth();
  const { mode: drawerBodyMode, showToggle: showModeToggle } = drawerModeFor(
    user?.featureFlags,
    drawerMode
  );
  const navigate = useNavigate();

  // Re-render when code selection changes (so "Selected code" stays up-to-date)
  useSyncExternalStore(
    subscribeCortexSelection,
    getCortexSelection,
    () => null
  );

  const activeMonacoTarget = useSyncExternalStore(
    subscribeCortexMonacoTarget,
    getCortexMonacoTargetForCurrentPage,
    () => null
  );
  const canAcceptToEditor = !!activeMonacoTarget?.editor;

  const extractCodeBlocks = useCallback((content: string): string[] => {
    const matches = Array.from(content.matchAll(/```[^\n]*\n([\s\S]*?)```/g));
    return matches
      .map(m => (m[1] ?? "").trim())
      .filter(block => block.length > 0);
  }, []);

  const [pendingApplyBlocks, setPendingApplyBlocks] = useState<string[] | null>(
    null
  );
  const [pendingApplySelectedIndex, setPendingApplySelectedIndex] = useState(0);

  useEffect(() => {
    if (!pendingApplyBlocks) return;
    setPendingApplySelectedIndex(0);
  }, [pendingApplyBlocks]);

  const handleApplySelectedBlock = useCallback(() => {
    if (!pendingApplyBlocks) return;
    const block = pendingApplyBlocks[pendingApplySelectedIndex] ?? "";
    if (!block) return;
    applyToCortexMonacoTarget(block);
    setPendingApplyBlocks(null);
  }, [pendingApplyBlocks, pendingApplySelectedIndex]);

  const handleAcceptToEditor = useCallback(
    (content: string) => {
      const blocks = extractCodeBlocks(content);
      if (blocks.length === 0) {
        setNavigationError(
          "No code block found to apply. Ask again and request the code in a fenced block (``` ... ```)."
        );
        return;
      }
      setNavigationError(null);
      if (blocks.length === 1) {
        applyToCortexMonacoTarget(blocks[0]);
        return;
      }
      setPendingApplyBlocks(blocks);
    },
    [extractCodeBlocks]
  );

  const [isMinimized, setIsMinimized] = useState(false);
  const [question, setQuestion] = useState("");
  const [hasUnread, setHasUnread] = useState(false);
  const [selectedModel, setSelectedModel] = useState<SelectOption | null>(null);
  const [selectedKnowledgeAssistant, setSelectedKnowledgeAssistant] = useState<{
    id: number;
    name: string;
    model: string;
  } | null>(null);
  const [showConversationList, setShowConversationList] = useState(false);
  const [showRouteChangePrompt, setShowRouteChangePrompt] = useState(false);
  const [hasDismissedRouteChangePrompt, setHasDismissedRouteChangePrompt] =
    useState(false);
  const [suppressNextRouteChangePrompt, setSuppressNextRouteChangePrompt] =
    useState(false);
  const [collapsedMessageIndexes, setCollapsedMessageIndexes] = useState<
    Set<number>
  >(() => new Set());
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [kbAssistantModelMismatch, setKbAssistantModelMismatch] = useState<
    string | null
  >(null);
  const [conversationPagePrompt, setConversationPagePrompt] = useState<{
    show: boolean;
    pageUrl: string;
    conversationId: string;
  } | null>(null);
  const [showContextMismatchBanner, setShowContextMismatchBanner] =
    useState(false);
  const [hasDismissedContextMismatch, setHasDismissedContextMismatch] =
    useState(false);
  const isActivePinned = activeConversation?.isPinned ?? false;
  const canTogglePin = !!activeConversation && !activeConversation.isPending;

  const { data: selectedToken } = useIntegrationTokenById(selectedTokenId);

  // Fetch Knowledge Builder assistants (for dropdown; selection resets when drawer closes)
  const { data: kbAssistants = [] } =
    useKnowledgeBuilderAssistantsForDrawer(isOpen);

  // Reset KB selection when drawer closes (no persistence)
  useEffect(() => {
    if (!isOpen) {
      setSelectedKnowledgeAssistant(null);
      setKbAssistantModelMismatch(null);
    }
  }, [isOpen]);

  // Fetch available AI models
  const { data: modelsData, isLoading: isModelsLoading } = useAvailableAIModels(
    selectedTokenId || undefined
  );

  // Convert models array to SelectOption format
  const modelOptions: SelectOption[] = useMemo(() => {
    if (!modelsData?.models) return [];
    return modelsData.models.map(model => ({
      label: model,
      value: model,
    }));
  }, [modelsData?.models]);

  const availableModelSet = useMemo(() => {
    return new Set(
      modelOptions
        .map(o => (typeof o.value === "string" ? o.value : null))
        .filter((v): v is string => !!v)
    );
  }, [modelOptions]);

  const kbAssistantOptions: KnowledgeBuilderAssistantOption[] = useMemo(() => {
    return kbAssistants.map(a => {
      const isCompatible = availableModelSet.has(a.model);
      return {
        label: isCompatible ? a.name : `${a.name} (requires ${a.model})`,
        value: String(a.id),
        isDisabled: !isCompatible,
        model: a.model,
      };
    });
  }, [kbAssistants, availableModelSet]);

  const preferredModelOption = useMemo(() => {
    if (!modelOptions.length) return null;
    for (const modelName of PREFERRED_MODELS) {
      const match = modelOptions.find(option => option.value === modelName);
      if (match) {
        return match;
      }
    }
    return null;
  }, [modelOptions]);

  // Reset selected model when selected token id changes
  useEffect(() => {
    setSelectedModel(null);
  }, [selectedTokenId]);

  // If KB assistant becomes incompatible after token/model list changes, clear it and warn.
  useEffect(() => {
    if (!selectedKnowledgeAssistant) return;
    if (availableModelSet.has(selectedKnowledgeAssistant.model)) return;
    setSelectedKnowledgeAssistant(null);
    setKbAssistantModelMismatch(
      "The selected Knowledge Builder assistant requires a different model than what's available for the currently selected token."
    );
  }, [availableModelSet, selectedKnowledgeAssistant]);

  const handleKnowledgeAssistantChange = useCallback(
    (val: SelectOption | null) => {
      const assistant = kbAssistants.find(a => String(a.id) === val?.value);
      if (assistant && !availableModelSet.has(assistant.model)) {
        setKbAssistantModelMismatch(
          "This Knowledge Builder assistant was created with a model that's not available for the currently selected token. Please switch token/model or choose a compatible assistant."
        );
        setSelectedKnowledgeAssistant(null);
        return;
      }

      setKbAssistantModelMismatch(null);
      setSelectedKnowledgeAssistant(
        assistant
          ? {
              id: assistant.id,
              name: assistant.name,
              model: assistant.model,
            }
          : null
      );
    },
    [availableModelSet, kbAssistants]
  );

  // Auto-select preferred model when available
  useEffect(() => {
    if (!selectedTokenId) return;
    if (!selectedModel && preferredModelOption) {
      setSelectedModel(preferredModelOption);
    }
  }, [selectedTokenId, selectedModel, preferredModelOption]);

  // Check if the selected model is still available
  useEffect(() => {
    if (!selectedModel) return;
    const isStillAvailable = modelOptions.some(
      option => option.value === selectedModel.value
    );
    if (!isStillAvailable) {
      setSelectedModel(null);
    }
  }, [modelOptions, selectedModel]);

  // Initialize: always create a new empty conversation when drawer opens
  // DB conversations will be listed, but not automatically loaded
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      // Reset initialization flag when drawer closes
      hasInitializedRef.current = false;
      return;
    }

    // Wait for API to finish loading before initializing
    if (conversationsLoading) return;

    // If we've already initialized, don't run again
    if (hasInitializedRef.current) return;

    // If there is no context yet, wait until a context is available.
    // This avoids "initializing" in a no-context state where no conversation
    // can be created, and ensures that when context later appears while the
    // drawer is already open, we still create the initial conversation.
    if (!currentContext) {
      return;
    }

    hasInitializedRef.current = true;

    // Always create a new empty conversation when drawer opens with context.
    // Even if DB conversations exist, user should see a fresh conversation.
    // User can select from conversation list to load existing conversations.
    const newId = createNewConversation();
    setActiveConversationId(newId);
    setOpenTabIds(prev => new Set([...prev, newId]));
  }, [
    isOpen,
    conversationsLoading,
    currentContext?.routePattern,
    currentContext?.contextName,
    createNewConversation,
    setOpenTabIds,
    setActiveConversationId,
  ]);

  // Detect context changes while drawer is open to suggest starting a new session
  const previousContextKeyRef = useRef<string | null>(null);
  const currentContextKey = useMemo(() => {
    if (!currentContext) return null;
    return `${currentContext.routePattern}::${currentContext.contextName}`;
  }, [currentContext]);

  // Get active conversation's history
  const activeConversationHistory = useMemo(() => {
    if (!activeConversation) return [];
    return activeConversation.conversationHistory || [];
  }, [activeConversation]);

  // UI-only: hide system/context messages from users (keep them in history for requests)
  const displayConversationHistory = useMemo(() => {
    return activeConversationHistory.filter(m => m.role !== "system");
  }, [activeConversationHistory]);

  useEffect(() => {
    const prevKey = previousContextKeyRef.current;

    if (!isOpen) {
      previousContextKeyRef.current = currentContextKey;
      setShowRouteChangePrompt(false);
      return;
    }

    // When route/context changes while drawer is open, suggest starting a new session.
    // Treat both context-to-context and context-to-null as changes.
    if (
      prevKey &&
      prevKey !== currentContextKey &&
      activeConversation &&
      displayConversationHistory.length > 0 &&
      !hasDismissedRouteChangePrompt
    ) {
      // Don't show if we just navigated via ConversationPagePrompt
      if (suppressNextRouteChangePrompt) {
        setSuppressNextRouteChangePrompt(false);
      } else {
        setShowRouteChangePrompt(true);
      }
    }

    previousContextKeyRef.current = currentContextKey;
  }, [
    isOpen,
    currentContextKey,
    activeConversation,
    displayConversationHistory.length,
    hasDismissedRouteChangePrompt,
    suppressNextRouteChangePrompt,
  ]);

  useEffect(() => {
    setCollapsedMessageIndexes(() => new Set());
  }, [activeConversationId]);

  const {
    isLoading,
    error,
    currentResponse,
    progressMessages,
    pendingMcpToolCall,
    isRespondingToMcpToolCall,
    respondToMcpToolCall,
    clearProgress,
    sendQuestion,
    clearError,
    clearConversation,
  } = useCortexStreaming({
    currentContext,
    selectedTokenId,
    selectedModel: selectedModel?.value as string | null,
    knowledgeAssistantId: selectedKnowledgeAssistant?.id ?? undefined,
    conversationId: activeConversationId,
    conversationHistory: activeConversationHistory,
    onUserMessage: content => {
      if (!activeConversationId) return;
      appendUserMessage(activeConversationId, content);
    },
    onAssistantMessage: content => {
      if (!activeConversationId) return;
      appendAssistantMessage(activeConversationId, content);
    },
    onMcpToolCallMessage: message => {
      if (!activeConversationId) return;
      appendMessage(activeConversationId, message);
    },
    onConversationCreated: realId => {
      if (!activeConversationId) return;

      // Replace temp ID with real ID in conversations map
      replaceConversationId(activeConversationId, realId);

      // Ensure tabs track the new real ID instead of the temp ID
      setOpenTabIds(prev => {
        if (!prev.has(activeConversationId)) return prev;
        const next = new Set(prev);
        next.delete(activeConversationId);
        next.add(realId);
        return next;
      });
    },
    activeConversation,
  });

  // Conversation management handlers
  const handleCreateNewConversation = () => {
    if (!currentContext) return;
    setShowRouteChangePrompt(false);
    setSuppressNextRouteChangePrompt(false);
    setNavigationError(null);
    setConversationPagePrompt(null);
    setShowContextMismatchBanner(false);
    setHasDismissedContextMismatch(false);
    const newId = createNewConversation();
    setOpenTabIds(prev => new Set([...prev, newId]));
    clearConversation();
  };

  // Helper function to actually load the conversation
  const loadConversation = useCallback(
    (id: string) => {
      setActiveConversationId(id);
      // Add to open tabs if not already open
      setOpenTabIds(prev => {
        if (prev.has(id)) return prev;
        return new Set([...prev, id]);
      });
      clearConversation();
    },
    [setActiveConversationId, setOpenTabIds, clearConversation]
  );

  const handleSelectConversation = (id: string) => {
    if (id === activeConversationId) return;

    // Clear any previous navigation errors and prompts
    setNavigationError(null);
    setConversationPagePrompt(null);

    // Check if this conversation is already open in a tab
    const isAlreadyOpen = openTabIds.has(id);

    // If already open, just switch to it without showing prompt
    if (isAlreadyOpen) {
      loadConversation(id);
      return;
    }

    // Get the conversation to check if it has a pageUrl (for first-time opening)
    const conversation = conversations.get(id);

    // Handle navigation prompt if pageUrl exists and conversation is being opened for first time
    if (conversation?.pageUrl) {
      // Validate pageUrl first
      if (!validatePageUrl(conversation.pageUrl)) {
        setNavigationError(
          "This conversation has an invalid page URL. Loading conversation without navigation."
        );
        console.error("Invalid pageUrl format:", conversation.pageUrl);
        // Load conversation anyway
        loadConversation(id);
        return;
      }

      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      // Only show prompt if we're not already on that page
      if (conversation.pageUrl !== currentPath) {
        // Show navigation prompt instead of auto-navigating
        setConversationPagePrompt({
          show: true,
          pageUrl: conversation.pageUrl,
          conversationId: id,
        });
        return;
      }
    }

    // If same page or no pageUrl, just load the conversation
    loadConversation(id);
  };

  // Handle navigation from the prompt
  const handleNavigateFromPrompt = useCallback(() => {
    if (!conversationPagePrompt) return;

    try {
      // Suppress RouteChangePrompt since this navigation is intentional
      setSuppressNextRouteChangePrompt(true);
      // Strip basename to avoid duplication (React Router adds it automatically)
      const pathWithoutBasename = stripBasename(conversationPagePrompt.pageUrl);
      navigate(pathWithoutBasename);
      loadConversation(conversationPagePrompt.conversationId);
      setConversationPagePrompt(null);
    } catch (error) {
      setNavigationError(
        "Could not navigate to the original page. Conversation loaded in current context."
      );
      console.error("Failed to navigate:", error);
      loadConversation(conversationPagePrompt.conversationId);
      setConversationPagePrompt(null);
    }
  }, [conversationPagePrompt, navigate, loadConversation]);

  // Handle dismissing the prompt (stay on current page)
  const handleDismissPagePrompt = useCallback(() => {
    if (!conversationPagePrompt) return;

    // Just load the conversation on the current page
    loadConversation(conversationPagePrompt.conversationId);
    setConversationPagePrompt(null);
    // Also dismiss the context mismatch banner to avoid showing it immediately after
    setHasDismissedContextMismatch(true);
  }, [conversationPagePrompt, loadConversation]);

  // Handle navigation from context mismatch banner
  const handleNavigateFromMismatchBanner = useCallback(() => {
    if (!activeConversation?.pageUrl) return;

    try {
      // Suppress RouteChangePrompt since this navigation is intentional
      setSuppressNextRouteChangePrompt(true);
      // Strip basename to avoid duplication (React Router adds it automatically)
      const pathWithoutBasename = stripBasename(activeConversation.pageUrl);
      navigate(pathWithoutBasename);
      setShowContextMismatchBanner(false);
    } catch (error) {
      console.error("Failed to navigate from context mismatch banner:", error);
      // Keep the banner visible if navigation fails
    }
  }, [activeConversation?.pageUrl, navigate]);

  // Detect context mismatch when active conversation changes
  useEffect(() => {
    // Don't show mismatch banner if:
    // 1. No active conversation
    // 2. Conversation has no pageUrl
    // 3. ConversationPagePrompt is already showing (to avoid duplicate banners)
    // 4. User has dismissed the context mismatch banner
    if (
      !activeConversation ||
      !activeConversation.pageUrl ||
      conversationPagePrompt?.show ||
      hasDismissedContextMismatch
    ) {
      setShowContextMismatchBanner(false);
      return;
    }

    const currentPath =
      window.location.pathname + window.location.search + window.location.hash;

    // Show banner if conversation belongs to a different page
    if (activeConversation.pageUrl !== currentPath) {
      setShowContextMismatchBanner(true);
    } else {
      setShowContextMismatchBanner(false);
    }
  }, [
    activeConversation,
    activeConversation?.pageUrl,
    conversationPagePrompt?.show,
    hasDismissedContextMismatch,
  ]);

  const handleCloseTab = (id: string) => {
    // Clear navigation error and prompts when closing tabs
    setNavigationError(null);
    setConversationPagePrompt(null);

    setOpenTabIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);

      // If closed tab was active, switch to another open tab or create new
      if (id === activeConversationId) {
        const remainingOpenTabs = Array.from(newSet);
        if (remainingOpenTabs.length > 0) {
          setActiveConversationId(remainingOpenTabs[0]);
        } else if (currentContext) {
          // Create new conversation if no tabs remain
          const newId = createNewConversation();
          newSet.add(newId);
        } else {
          setActiveConversationId(null);
        }
        clearConversation();
      }

      return newSet;
    });
  };

  const handleDeleteConversation = async (id: string) => {
    // Clear navigation error and prompts when deleting conversations
    setNavigationError(null);
    setConversationPagePrompt(null);

    await deleteConversation(id);
    setOpenTabIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });

    // If deleted conversation was active, switch to another open tab or create new
    if (id === activeConversationId) {
      const remainingOpenTabs = Array.from(openTabIds).filter(
        tabId => tabId !== id
      );
      if (remainingOpenTabs.length > 0) {
        setActiveConversationId(remainingOpenTabs[0]);
      } else if (currentContext) {
        // Create new conversation if no tabs remain
        const newId = createNewConversation();
        setOpenTabIds(() => new Set([newId]));
      } else {
        setActiveConversationId(null);
      }
      clearConversation();
    }
  };

  const handleRenameConversation = async (id: string, title: string | null) => {
    if (!id || !title) return;
    if (
      activeConversation?.title &&
      activeConversation.title.trim() === title.trim()
    )
      return;
    await updateConversationMeta(id, { title });
  };

  const toggleMessageCollapsed = useCallback((index: number) => {
    setCollapsedMessageIndexes(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Filter conversations to only show those with open tabs
  const openTabConversations = useMemo(() => {
    return Array.from(conversations.values()).filter(conv =>
      openTabIds.has(conv.id)
    );
  }, [conversations, openTabIds]);

  const handleConversationSearchChange = (value: string) => {
    setConversationListPage(0);
    setConversationListSearchKey(value);
  };

  const handlePinnedOnlyChange = (value: boolean) => {
    setConversationListPage(0);
    setConversationListPinnedOnly(value);
  };

  const handleTogglePinned = useCallback(
    async (id: string, nextPinned: boolean) => {
      await updateConversationMeta(id, { isPinned: nextPinned });
    },
    [updateConversationMeta]
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const progressEndRef = useRef<HTMLDivElement>(null);
  const lastAssistantCountRef = useRef<number>(0);
  const lastResponseRef = useRef<string>("");

  // Auto-scroll to bottom when new messages arrive or tool confirmation appears
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeConversationHistory, currentResponse, pendingMcpToolCall]);

  // Auto-scroll the progress list specifically
  useEffect(() => {
    progressEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [progressMessages]);

  // While minimized, detect new AI content to toggle unread
  useEffect(() => {
    if (!isMinimized) return;

    const assistantCount = activeConversationHistory.filter(
      m => m.role === "assistant"
    ).length;

    if (assistantCount > lastAssistantCountRef.current) {
      setHasUnread(true);
      return;
    }

    if (currentResponse && currentResponse !== lastResponseRef.current) {
      setHasUnread(true);
    }
  }, [isMinimized, activeConversationHistory, currentResponse]);

  const handleMinimize = () => {
    lastAssistantCountRef.current = activeConversationHistory.filter(
      m => m.role === "assistant"
    ).length;
    lastResponseRef.current = currentResponse;
    setHasUnread(false);
    setIsMinimized(true);
    onMinimizedChange?.(true);
  };

  const handleRestore = () => {
    setHasUnread(false);
    lastAssistantCountRef.current = activeConversationHistory.filter(
      m => m.role === "assistant"
    ).length;
    lastResponseRef.current = currentResponse;
    setIsMinimized(false);
    onMinimizedChange?.(false);
  };

  const handleClose = () => {
    setIsMinimized(false);
    setQuestion("");
    clearConversation();
    clearProgress();
    onMinimizedChange?.(false);
    onClose();
  };

  // Get context data preview
  const contextData = currentContext?.getContext?.();

  const fileExtension = contextData?.reportExtension;
  const fileSize = contextData?.reportFileSize;
  const isReportContext = fileExtension && fileSize;

  const isAnthropicMissingEmbeddingSelection = useMemo(() => {
    // Anthropic needs an embeddings-provider token for:
    // - report Q&A (file context)
    // - Knowledge Builder (RAG semantic search)
    if (!isReportContext && !selectedKnowledgeAssistant) return false;
    if (
      !selectedToken ||
      selectedToken.tokenType !== ServiceTokenType.Anthropic
    )
      return false;

    const embeddingId =
      (
        selectedToken.credentialData as {
          embeddingServiceTokenId?: number | null;
        }
      )?.embeddingServiceTokenId ?? null;
    return embeddingId === null || embeddingId === undefined;
  }, [isReportContext, selectedKnowledgeAssistant, selectedToken]);

  const isInsightEnabled = useMemo(() => {
    if (!isReportContext) {
      return true;
    }
    return enableAiInsight(fileExtension, fileSize, user);
  }, [isReportContext, fileExtension, fileSize, user]);

  const handleSendQuestion = async () => {
    if (
      !question.trim() ||
      isLoading ||
      !selectedTokenId ||
      !isInsightEnabled ||
      isAnthropicMissingEmbeddingSelection ||
      !currentContext
    ) {
      return;
    }

    const questionText = question.trim();
    setQuestion("");
    await sendQuestion(questionText);
  };

  return (
    <Drawer
      open={isOpen}
      setOpen={handleClose}
      isMinimized={isMinimized}
      onMinimize={handleMinimize}
      onRestore={handleRestore}
      title={drawerBodyMode === "claude" ? "Claude Code" : "AI Insight"}
      allowBackgroundInteraction={true}
      minimizedHighlight={isMinimized && hasUnread}
      usePortal={usePortal}
      resizable={true}
      onWidthChange={onWidthChange}>
      <div className="flex h-full flex-col bg-gradient-to-br from-white via-gray-50 to-white">
        {showModeToggle && (
          <DrawerModeToggle mode={drawerBodyMode} onChange={setDrawerMode} />
        )}
        <DrawerModeBodies
          mode={drawerBodyMode}
          claude={<ClaudeCodePanel />}
          insight={
            <>
              {/* Conversation Tabs */}
              {openTabConversations.length > 0 && !showConversationList && (
                <ConversationTabs
                  conversations={openTabConversations}
                  activeConversationId={activeConversationId}
                  onSelectConversation={handleSelectConversation}
                  onCreateNew={handleCreateNewConversation}
                  onCloseTab={handleCloseTab}
                  onRenameConversation={handleRenameConversation}
                />
              )}

              {/* Conversation List or Context + Chat */}
              {showConversationList ? (
                <ConversationList
                  conversations={pagedConversations}
                  activeConversationId={activeConversationId}
                  onSelectConversation={handleSelectConversation}
                  onDeleteConversation={handleDeleteConversation}
                  onCreateNew={handleCreateNewConversation}
                  isOpen={showConversationList}
                  onClose={() => setShowConversationList(false)}
                  onRenameConversation={handleRenameConversation}
                  onTogglePinned={handleTogglePinned}
                  page={conversationListPage}
                  pageSize={conversationListPageSize}
                  total={conversationListTotal}
                  onPageChange={setConversationListPage}
                  onPageSizeChange={setConversationListPageSize}
                  searchQuery={conversationListSearchKey}
                  onSearchQueryChange={handleConversationSearchChange}
                  pinnedOnly={conversationListPinnedOnly}
                  onPinnedOnlyChange={handlePinnedOnlyChange}
                />
              ) : (
                <>
                  {/* Context Information Section - Enhanced */}
                  <section className="p-4 pb-3 border-b border-gray-200/60 bg-gradient-to-r from-indigo-50/30 via-white to-purple-50/30 backdrop-blur-sm">
                    <div className="mt-1">
                      {/* Conversation Header Actions: pin + list button */}
                      {conversations.size > 0 && (
                        <div className="flex justify-end mb-2">
                          <div className="flex items-center gap-2">
                            {canTogglePin && (
                              <button
                                type="button"
                                onClick={() =>
                                  activeConversation &&
                                  handleTogglePinned(
                                    activeConversation.id,
                                    !isActivePinned
                                  )
                                }
                                className={classNames(
                                  "flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors",
                                  isActivePinned
                                    ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                    : "border-gray-300 text-gray-600 hover:bg-gray-100"
                                )}>
                                <StarIcon
                                  className={classNames(
                                    "h-4 w-4",
                                    isActivePinned
                                      ? "text-yellow-400"
                                      : "text-gray-400"
                                  )}
                                />
                                <span>{isActivePinned ? "Pinned" : "Pin"}</span>
                              </button>
                            )}
                            <ConversationListButton
                              onClick={() => setShowConversationList(true)}
                            />
                          </div>
                        </div>
                      )}
                      <div className="space-y-3">
                        {currentContext && (
                          <>
                            {currentContext.customContent && (
                              <div className="mt-2">
                                {currentContext.customContent}
                              </div>
                            )}
                          </>
                        )}
                        {typeof contextData?.selectedCode === "string" &&
                          contextData.selectedCode.trim().length > 0 && (
                            <Accordion
                              title="Selected code"
                              bgColor="bg-white"
                              textColor="text-gray-800"
                              initialOpen={true}>
                              <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words text-xs font-mono text-gray-800">
                                {contextData.selectedCode}
                              </pre>
                            </Accordion>
                          )}
                        {/* Knowledge Builder Assistant Dropdown (optional, no persistence) */}
                        {selectedTokenId && kbAssistantOptions.length > 0 && (
                          <div className="mt-3">
                            <SelectComponent
                              name="knowledgeAssistant"
                              placeholder="Select Knowledge Builder"
                              options={kbAssistantOptions}
                              readOnly={isLoading}
                              enableClear={true}
                              value={
                                selectedKnowledgeAssistant
                                  ? {
                                      label: selectedKnowledgeAssistant.name,
                                      value: String(
                                        selectedKnowledgeAssistant.id
                                      ),
                                    }
                                  : null
                              }
                              onChange={val =>
                                handleKnowledgeAssistantChange(
                                  val as SelectOption | null
                                )
                              }
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Availability depends on your selected token/model.
                            </p>
                            {kbAssistantModelMismatch && (
                              <div className="mt-2">
                                <MessageBanner
                                  messages={[kbAssistantModelMismatch]}
                                  type={BannerType.Warning}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {/* Model Selection Dropdown */}
                        {selectedTokenId && (
                          <div className="mt-3">
                            <SelectComponent
                              name="model"
                              placeholder="Select AI Model"
                              options={modelOptions}
                              readOnly={
                                isLoading || isModelsLoading || !selectedTokenId
                              }
                              enableClear={false}
                              isLoading={isModelsLoading}
                              value={selectedModel}
                              onChange={val =>
                                setSelectedModel(val as SelectOption | null)
                              }
                            />
                          </div>
                        )}
                        {isReportContext && !isInsightEnabled && (
                          <MessageBanner
                            messages={[
                              "AI insight is not available for this file type yet.",
                            ]}
                            type={BannerType.Warning}
                          />
                        )}
                        {isAnthropicMissingEmbeddingSelection && (
                          <MessageBanner
                            messages={[
                              "This Claude token is missing an embeddings provider token. Edit the token and select an OpenAI or Gemini token for embeddings to enable report Q&A.",
                            ]}
                            type={BannerType.Warning}
                          />
                        )}
                        {navigationError && (
                          <MessageBanner
                            messages={[navigationError]}
                            type={BannerType.Warning}
                          />
                        )}

                        {pendingApplyBlocks && (
                          <ApplySnippetPicker
                            snippets={pendingApplyBlocks}
                            selectedIndex={pendingApplySelectedIndex}
                            onSelectIndex={setPendingApplySelectedIndex}
                            onCancel={() => setPendingApplyBlocks(null)}
                            onApply={handleApplySelectedBlock}
                          />
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Chat Messages Section - Enhanced */}
                  <section className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-gray-50/50 to-white">
                    <div className="flex flex-col gap-4">
                      {/* Welcome Message - Enhanced */}
                      {displayConversationHistory.length === 0 &&
                        !isLoading && (
                          <div className="group relative p-5 pt-7 bg-gradient-to-br from-indigo-100 to-purple-100 text-gray-800 self-start rounded-2xl rounded-tl-sm max-w-[85%] shadow-md hover:shadow-lg transition-all duration-300 border border-indigo-200/30">
                            <div className="absolute top-2 left-3">
                              <SparklesIcon className="h-4 w-4 text-indigo-500 opacity-60" />
                            </div>
                            <p className="text-sm font-medium leading-relaxed">
                              Hello! I&apos;m your AI assistant. How can I help
                              you?
                            </p>
                          </div>
                        )}

                      {/* Conversation History - Enhanced */}
                      {displayConversationHistory.map((message, index) => {
                        if (message.mcpToolCall) {
                          return (
                            <McpToolCallBubble
                              key={index}
                              mcpToolCall={message.mcpToolCall}
                            />
                          );
                        }

                        const isLong =
                          message.content.length > 400 ||
                          message.content.split("\n").length > 5;
                        // Long messages should be expanded by default.
                        const isExpanded =
                          !isLong || !collapsedMessageIndexes.has(index);

                        return (
                          <CortexMessageBubble
                            key={index}
                            message={message}
                            index={index}
                            isLong={isLong}
                            isExpanded={isExpanded}
                            onToggleExpanded={toggleMessageCollapsed}
                            canAcceptToEditor={
                              canAcceptToEditor &&
                              message.role === "assistant" &&
                              extractCodeBlocks(message.content).length > 0
                            }
                            onAcceptToEditor={handleAcceptToEditor}
                          />
                        );
                      })}

                      {/* MCP Tool Call Confirmation */}
                      {pendingMcpToolCall && (
                        <McpToolConfirmation
                          pendingCall={pendingMcpToolCall}
                          isResponding={isRespondingToMcpToolCall}
                          onAccept={callId =>
                            respondToMcpToolCall(callId, true)
                          }
                          onReject={callId =>
                            respondToMcpToolCall(callId, false)
                          }
                        />
                      )}

                      {/* Current AI Response (streaming) - Enhanced */}
                      {currentResponse && currentResponse.trim().length > 0 && (
                        <div className="relative self-start p-5 pt-7 bg-gradient-to-br from-indigo-100 to-purple-100 text-gray-800 rounded-2xl rounded-tl-sm max-w-[85%] shadow-md border border-indigo-200/50">
                          <div className="absolute top-2 left-3">
                            <SparklesIcon className="h-4 w-4 text-indigo-400 opacity-50" />
                          </div>
                          <MarkdownMessage
                            content={currentResponse}
                            className="text-sm leading-relaxed"
                          />
                        </div>
                      )}

                      {/* Error Display - Enhanced */}
                      {error && (
                        <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 text-red-800 rounded-r-lg shadow-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <XCircleIcon
                                className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors"
                                onClick={clearError}
                              />
                            </div>
                            <div className="ml-3 flex-1">
                              <p className="text-sm font-semibold">Error</p>
                              <p className="text-sm mt-1 text-red-700 font-sans">
                                {error}
                              </p>
                              <button
                                onClick={clearError}
                                className="mt-2 text-sm text-red-600 hover:text-red-800 underline font-medium transition-colors">
                                Dismiss
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Backend progress / status log (during report processing) */}
                      {isLoading && progressMessages.length > 0 && (
                        <div className="flex max-h-32 flex-col gap-2 overflow-y-auto px-2 py-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                          {progressMessages.map((line, idx, arr) => {
                            const isLast = idx === arr.length - 1;
                            return (
                              <div
                                key={`${idx}-${line}`}
                                className={classNames(
                                  "flex items-center gap-2 text-xs transition-opacity duration-300",
                                  isLast
                                    ? "text-indigo-600 font-medium"
                                    : "text-gray-400 opacity-70"
                                )}>
                                {isLast ? (
                                  <div className="flex h-3 w-3 items-center justify-center">
                                    <div className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
                                  </div>
                                ) : (
                                  <div className="flex h-3 w-3 items-center justify-center">
                                    <div className="h-1 w-1 rounded-full bg-gray-300" />
                                  </div>
                                )}
                                <span className="break-words">{line}</span>
                              </div>
                            );
                          })}
                          <div ref={progressEndRef} />
                        </div>
                      )}

                      {/* AI Thinking Animation */}
                      {isLoading && <ThinkingAnimation />}
                      <div ref={messagesEndRef} />
                    </div>
                  </section>

                  {/* Input Section - Enhanced */}
                  <section className="relative px-4 py-4 border-t border-gray-200/60 bg-white/80 backdrop-blur-sm">
                    <div className="relative">
                      {/* Conversation Page Navigation Prompt (bottom) */}
                      {conversationPagePrompt?.show && (
                        <ConversationPagePrompt
                          visible={conversationPagePrompt.show}
                          pageUrl={conversationPagePrompt.pageUrl}
                          onNavigate={handleNavigateFromPrompt}
                          onDismiss={handleDismissPagePrompt}
                        />
                      )}

                      {/* Context Mismatch Banner (bottom) */}
                      {showContextMismatchBanner &&
                        activeConversation?.pageUrl && (
                          <ContextMismatchBanner
                            visible={showContextMismatchBanner}
                            onNavigate={handleNavigateFromMismatchBanner}
                            onDismiss={() => {
                              setShowContextMismatchBanner(false);
                              setHasDismissedContextMismatch(true);
                            }}
                          />
                        )}

                      {/* Route change prompt - suggest starting a new session (bottom) */}
                      <RouteChangePrompt
                        visible={showRouteChangePrompt}
                        onStartNewSession={handleCreateNewConversation}
                        onDismiss={() => {
                          setShowRouteChangePrompt(false);
                          setHasDismissedRouteChangePrompt(true);
                        }}
                      />

                      <textarea
                        value={question}
                        disabled={
                          isLoading ||
                          !selectedTokenId ||
                          !isInsightEnabled ||
                          isAnthropicMissingEmbeddingSelection ||
                          !currentContext
                        }
                        onChange={e => setQuestion(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendQuestion();
                          }
                        }}
                        placeholder={
                          !selectedTokenId
                            ? "Please select a token first"
                            : isAnthropicMissingEmbeddingSelection
                              ? "Select an embeddings provider token for Claude (Profile → Integration Tokens)"
                              : currentContext
                                ? "Ask a question about the current context..."
                                : "AI Insight is only available on pages with context."
                        }
                        className="block w-full rounded-xl border-0 p-4 pr-14 text-gray-900 shadow-md ring-1 ring-inset ring-gray-300/50 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:shadow-lg transition-all duration-200 sm:text-sm sm:leading-6 resize-none disabled:opacity-50 disabled:cursor-not-allowed bg-white/90 backdrop-blur-sm"
                        rows={3}
                        autoFocus
                      />
                      <button
                        onClick={handleSendQuestion}
                        disabled={
                          isLoading ||
                          !question.trim() ||
                          !selectedTokenId ||
                          !isInsightEnabled ||
                          isAnthropicMissingEmbeddingSelection ||
                          !currentContext
                        }
                        className="absolute right-3 bottom-5 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:from-indigo-700 hover:to-indigo-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95">
                        <span className="text-lg">↑</span>
                      </button>
                    </div>
                    {isAnthropicMissingEmbeddingSelection && (
                      <button
                        type="button"
                        onClick={() => navigate(ROUTES.INTEGRATION_TOKENS)}
                        className="mt-2 text-xs font-medium text-indigo-700 underline hover:text-indigo-900">
                        Go to Integration Tokens
                      </button>
                    )}
                  </section>
                </>
              )}
            </>
          }
        />
      </div>
    </Drawer>
  );
};

export default CortexDrawer;
